import { IconCrosshair } from "@tabler/icons-react";
import axios from "axios";
import { useState, useRef } from "react";
import toast from "react-hot-toast";
import { NotificationsProvider, showNotification } from '@mantine/notifications';
import { useForm } from "@mantine/form";
import { createEmotionCache, Container, Paper, ColorSchemeProvider, MantineProvider, Button, TextInput, Center, CheckIcon, Text, Textarea, Checkbox, Title, Anchor, Group } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { Route, BrowserRouter, Routes, useNavigate } from "react-router-dom";
import AppLayout  from "./components/AppLayout";
import Map from "./components/Map";
import { MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet";
import content from './ambassadorForm.json';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-markercluster/styles'
import L from "leaflet";

const googleMarkerIcon = new L.Icon({
    iconUrl:
      "https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2.png",
    iconSize: [27, 40],
    iconAnchor: [13, 40],
    popupAnchor: [0, -40],
  });

export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          resolve({ latitude, longitude });
        },
        (error) => {
          reject(error);
        }
      );
    } else {
      reject(new Error('Geolocation is not supported by this browser.'));
    }
  });
}


function App() {
  const [colorScheme, setColorScheme] = useLocalStorage({
    key: "_oo_theme",
    defaultValue: "light",
    getInitialValueInEffect: true
  });

  const toggleColorScheme = (value) => {
    setColorScheme(value || (colorScheme === "light" ? "dark" : "light"));
  };

  const myCache = createEmotionCache({ key: "ooemc"});

  const HomePage = () => {
    const [loading, setLoading] = useState(false);
    const [fromMap, setFromMap] = useState(false);
    const [checked, setChecked] = useState(false);
    const [checked2, setChecked2] = useState(false);
    const [coords, setCoords] = useState({
      lat: null,
      lng: null
    });

    const navigate = useNavigate();

    const form = useForm({
      initialValues: {
        name: "",
        email: "",
        country: "",
        commitment: "",
        latitude: "",
        longitude: ""
      },
      validate: {
        email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
        name: (value) => value.length > 0 ? null : "Invalid name",
        country: (value) => value.length > 0 ? null : "Missing field required",
        longitude: (value) => value.length > 0 ? null : "Invalid longitude value",
        latitude: (value) => value.length > 0 ? null : "Invalid latitude"
      }
    });

    const getCoords = () => {
      if (!navigator.geolocation) {
        showNotification({
          title: "Not Supported",
          message: "Geolocation is not supported by your browser.",
          color: "red",
        });
        return;
      }

      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "denied") {
          showNotification({
            title: "Location Access Blocked",
            message: "Please enable location access in your browser or device settings. You can also pin your location manually on the map.",
            color: "red",
          });
          return;
        }

        getCurrentPosition()
          .then((position) => {
            form.setFieldValue("latitude", position?.latitude.toString());
            form.setFieldValue("longitude", position.longitude.toString());
            setCoords({
              lat: position?.latitude.toString(),
              lng: position.longitude.toString(),
            });
          })
          .catch((error) => {
            console.error("Error getting position:", error);
            showNotification({
              title: "Location Error",
              message: "Unable to retrieve your location. Please pin it manually on the map.",
              color: "red",
            });
          });
      });
    };

    const onSubmit = async (obj) => {
      setLoading(true);
      try {
        const body = {
          name: obj.name,
          email: obj.email,
          country: obj.country,
          commitment: obj.commitment,
          latitude: Number(obj.latitude),
          longitude: Number(obj.longitude)
        };

        await axios.post("https://goldenruleismcan.org/server/create_poi.php", body)
          .then(function(res) {
            setLoading(false);
            toast.success("Data submitted successfully");
            navigate("/thank-you");
          });
      } catch(error) {
        setLoading(false);
        toast.error("An issue occured while submitting the data");
      }
    };

    const LocationPicker = ({ onLocationSelect }) => {
      useMapEvents({
        dblclick(e) {
          const { lat, lng } = e.latlng;
          onLocationSelect({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
        },
      });
      return null;
    };

    const DraggableMarker = ({ position, onDragEnd }) => {
      const markerRef = useRef(null);
      return (
        <Marker
          icon={googleMarkerIcon}
          position={position}
          draggable={true}
          ref={markerRef}
          eventHandlers={{
            dragend() {
              const marker = markerRef.current;
              if (marker) {
                const { lat, lng } = marker.getLatLng();
                onDragEnd({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
              }
            },
          }}
        />
      );
    };

    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
      iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    });

    const handleCoordsUpdate = (newCoords) => {
      setCoords(newCoords);
      form.setFieldValue("latitude", newCoords.lat.toString());
      form.setFieldValue("longitude", newCoords.lng.toString());
    };

    return (
      <Container size={600}>
        <Paper mt={10} p="md">
          <Title color="#1a235e" mb="md" order={5}>
            <span style={{ color: "#ecc73b" }}>GOLDENRULEISM</span> AMBASSADORS MAP
          </Title>
          <Text color="#1a235e" size={"lg"} mb="md">
            I affirm the two principal principles of <span style={{ color: "#ecc73b" }}>GoldenRuleism</span>: Do for all others, both directly and indirectly, what you would want done for you. Don't do to any others, either directly or indirectly, what you wouldn't want done to you.
          </Text>
          <form onSubmit={form.onSubmit((values) => { onSubmit(values) })}>
            <TextInput color="#1a235e" size={"lg"} styles={{ input: { '::placeholder': { fontWeight: 300 } } }} mb="sm" height={40} withAsterisk {...form.getInputProps("name")} placeholder="" label={content.fields.name} required />
            <TextInput color="#1a235e" size={"lg"} styles={{ input: { '::placeholder': { fontWeight: 300 } } }} mb="sm" height={40} withAsterisk {...form.getInputProps("country")} placeholder="" label={content.fields.country} required />
            <TextInput color="#1a235e" size={"lg"} styles={{ input: { '::placeholder': { fontWeight: 300 } } }} mb="sm" height={40} withAsterisk {...form.getInputProps("email")} placeholder="" label={content.fields.email} required />
                  <Text size={"lg"}>
                    {content.map.instruction}
                  </Text>
                  <br />
                  <Text size={"lg"}>
                    {content.map.instruction1}
                  </Text>
                  <br />
            <Checkbox color="#1a235e" size={"lg"} mb="sm" checked={fromMap} onChange={(e) => { setFromMap(e.currentTarget.checked) }} label={content.checkboxes.useCurrentLocation} />
                  <br />
                  <Text size={"lg"}>
                    {content.map.instruction2}
                  </Text>
            {!fromMap && (
              <Group mb="sm">
                <div className="w-full">
                  <MapContainer
                    doubleClickZoom={false}
                    attributionControl={false}
                    center={[15.375592, 36.749879]}
                    zoom={2}
                    style={{ height: "300px", width: "100%", backgroundColor: "white" }}
                  >
                    <LocationPicker onLocationSelect={handleCoordsUpdate} />
                    {coords.lat && coords.lng && (
                      <DraggableMarker
                        position={[coords.lat, coords.lng]}
                        onDragEnd={handleCoordsUpdate}
                      />
                    )}
                    <TileLayer
                      url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                      subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                      maxZoom={20}
                    />
                  </MapContainer>
                </div>
              </Group>
            )}

            {fromMap && (
              <Group>
                <Button color="#1a235e" size={"lg"} onClick={() => { getCoords() }} mt="md" mb="md" variant="default" leftIcon={<IconCrosshair />}>
                  {content.labels.pinLocation}
                </Button>
                {coords.lat && coords.lng && (
                    <MapContainer
                    doubleClickZoom={false}
                    attributionControl={false}
                    center={[15.375592, 36.749879]}
                    zoom={2}
                    style={{ height: "300px", width: "100%", backgroundColor: "white" }}
                  >
                    {coords.lat && coords.lng && (
                      <DraggableMarker
                        position={[coords.lat, coords.lng]}
                        onDragEnd={handleCoordsUpdate}
                      />
                    )}
                    <TileLayer
                      url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                      subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                      maxZoom={20}
                    />
                  </MapContainer>
                )}
              </Group>
            )}

            <Textarea color="#1a235e" size={"lg"} minRows={2} styles={{ input: { '::placeholder': { fontWeight: 300 } } }} mb="sm" height={40} {...form.getInputProps("commitment")} placeholder="" label={content.fields.commitment} />
            <Checkbox color="#1a235e" size={"lg"} checked={checked2} mb="sm" onChange={(e) => { setChecked2(e.currentTarget.checked) }} label={content.checkboxes.selfAssessment} />
            <Anchor
              mb="sm"
              href="https://goldenruleismcan.org/tearms-and-conditions/"
              target="_blank"
              color="#1a235e"
              size={"lg"}
            >
              {content.labels.termsLink}
            </Anchor>
            <Checkbox color="#1a235e" size={"lg"} mt="sm" checked={checked} onChange={(e) => { setChecked(e.currentTarget.checked) }} label={content.checkboxes.termsAcceptance} />
            <Button color="#1a235e" size={"lg"} disabled={!checked || !checked2} type="submit" loading={loading} mt="lg" fullWidth>
              {content.labels.submit}
            </Button>
          </form>
        </Paper>
      </Container>
    );
  };

  const ThankYou = () => {
    return (
      <Container size={600}>
        <Paper mt={100} p="md">
          <Center>
            <CheckIcon width={40} height={40} color="green" />
          </Center>
          <Text color="#1a235e" size={"lg"} align="center" mt="md">
            Thank you for submitting your location as a Goldenruleism ambassador; your commitment to spreading kindness and compassion is truly appreciated!
          </Text>
        </Paper>
      </Container>
    );
  };

  return (
    <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
      <MantineProvider
        theme={{
          colorScheme: colorScheme,
          primaryColor: "indigo",
          primaryShade: 9,
          white: 'white',
          black: 'black',
          cursorType: "pointer",
        }}
        withGlobalStyles
        withNormalizeCSS
        emotionCache={myCache}
      >
        <NotificationsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<AppLayout />}>
                <Route index element={<Map />} />
                <Route path="/new-entry" element={<HomePage />} />
                <Route path="/thank-you" element={<ThankYou />} />
                <Route path="/map" element={<Map />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </NotificationsProvider>
      </MantineProvider>
    </ColorSchemeProvider>
  );
}

export default App;
